import { GetCardTransactionDetailById } from '@app/finxone-web-frontend/app/lib/actions/card-management.action';
import {
  AddCardRequest,
  CardAccountType,
  CardInfoType,
  CardLimitsType,
  CardStatusType,
  CardTransactionDetail,
  CardTransactionInfoType,
  CardType,
  CustomFreezeResponse,
  PaginatedResponse,
  SecureCardInfoResponse,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { Observable, Subject, of } from 'rxjs';

export class MockCardManagementService {
  constructor(private store: Store) {}
  public otpCode = '123456';
  sendDataToBottomPopup: Subject<{
    popupIndex: number;
    isFullPagePopup: boolean;
  }> = new Subject();
  getCards(): Observable<PaginatedResponse<CardInfoType>> {
    // '123456' is this mock's own card id (the result row below).
    this.store.dispatch(new GetCardTransactionDetailById('123456', '4ebd8a5a-2e73-448f-b97b-a57b2a17ef1a'));
    return of({
      result: [
        {
          id: '123456',
          availableBalance: '36000.42',
          name: 'John Doe',
          expiry: '2029-01-31',
          maskedPan: '****-****-****-0123',
          type: CardType.VIRTUAL,
          status: CardStatusType.ACTIVE,
          accountType: CardAccountType.TOP_UP,
          createdDate: '01/01/2024',
          activatedDate: '01/01/2024',
        },
        {
          id: '654321',
          availableBalance: '36000.42',
          name: 'John Doe',
          expiry: '2035-12-31',
          maskedPan: '****-****-****-0987',
          type: CardType.VIRTUAL,
          status: CardStatusType.SUSPENDED,
          accountType: CardAccountType.DEBIT,
          createdDate: '01/08/2024',
          activatedDate: '01/08/2024',
        },
        {
          id: '87654321',
          availableBalance: '36000.42',
          name: 'John Doe',
          expiry: '2026-04-3',
          maskedPan: '****-****-****-0420',
          type: CardType.PHYSICAL,
          status: CardStatusType.INACTIVE,
          accountType: CardAccountType.TOP_UP,
          createdDate: '11/11/2022',
        },
        {
          id: '987654321',
          availableBalance: '36000.42',
          name: 'John Doe',
          expiry: '2026-04-30',
          maskedPan: '****-****-****-0421',
          type: CardType.PHYSICAL,
          status: CardStatusType.PENDING,
          accountType: CardAccountType.DEBIT,
          createdDate: '11/11/2022',
        },
      ],
      totalPages: 1,
      page: 1,
      limit: 10,
      totalItems: 4,
    } as PaginatedResponse<CardInfoType>);
  }

  public getCardLimits(cardId: string): Observable<CardLimitsType[]> {
    return of([
      {
        trxGroup: 'TRANSACTION',
        limitType: 'MONTHLY',
        amount: {
          value: '60.00',
          availableValue: '3.50',
          currency: 'EUR',
          spendValue: '56.50',
        },
      },
      {
        trxGroup: 'TRANSACTION',
        limitType: 'DAILY',
        amount: {
          value: '30.00',
          availableValue: '1.50',
          currency: 'EUR',
          spendValue: '5.50',
        },
      },
      {
        trxGroup: 'ATM',
        limitType: 'MONTHLY',
        amount: {
          value: '200.00',
          availableValue: '180.00',
          currency: 'EUR',
          spendValue: '20.00',
        },
      },
      {
        trxGroup: 'ATM',
        limitType: 'DAILY',
        amount: {
          value: '20.00',
          availableValue: '18.00',
          currency: 'EUR',
          spendValue: '2.00',
        },
      },
    ]);
  }
  getCVVDetails(): Observable<SecureCardInfoResponse> {
    return of({
      pan: '1234567891234567',
      cvv2: '123',
      pin: '9876',
    } as SecureCardInfoResponse);
  }

  // POST /cards/freeze answers CustomFreezeResponse — the status lives under
  // `data`, which is where every caller (native and the custom-widget bridge)
  // reads it from.
  freezeCard(request?: { accountId?: string; cardId: string }): Observable<CustomFreezeResponse> {
    return of({ data: { status: CardStatusType.FROZEN }, alertCodes: [] });
  }

  unfreezeCard(request?: { accountId?: string; cardId: string }): Observable<CustomFreezeResponse> {
    return of({ data: { status: CardStatusType.ACTIVE }, alertCodes: [] });
  }

  // POST /cards echoes the request body back; the new card is only discoverable
  // through getCards() — mirroring the real service, which types the response
  // as AddCardRequest.
  public addCard(payload: AddCardRequest, accessToken?: string): Observable<AddCardRequest> {
    return of({ ...payload });
  }

  public getCardTransaction(): Observable<PaginatedResponse<CardTransactionInfoType>> {
    return of({
      result: [
        {
          id: '06292609-426f-4a5e-afef-bf320231976a',
          cardId: '5fed064a-8cd8-4a84-8dfc-1bc92e2fc650',
          transactionNumber: '360335',
          type: 'atm-withdrawal',
          status: 'settled',
          transactionAmount: -5,
          transactionCurrency: 'EUR',
          merchant: {
            name: 'Groceries',
            id: 'e26a5a63-17c8-4c8a-9bf5-b847385ea6f3',
          },
          totalAmount: -5,
          creationDateTime: '2024-11-20T09:45:01.036000',
        },
        {
          id: '0935c52a-c6c0-49d3-9ff8-59c4d64fd0a0',
          cardId: '5fed064a-8cd8-4a84-8dfc-1bc92e2fc650',
          matchingTransactionId: 'CR241122000026',
          transactionNumber: '361407',
          type: 'chargeback',
          status: 'settled',
          transactionAmount: 100,
          transactionCurrency: 'EUR',
          originalCurrencyAmount: 500,
          originalCurrency: 'USD',
          merchant: {
            name: 'Boots Pharmacy',
            id: 'be10579f-4274-43d9-8c8d-867111941209',
          },
          totalAmount: 100,
          creationDateTime: '2024-11-22T16:58:26.645688',
        },
        {
          id: '360cc01a-bc94-4aa9-b276-b2ab764ba609',
          cardId: '5fed064a-8cd8-4a84-8dfc-1bc92e2fc650',
          matchingTransactionId: 'CR241122000024',
          transactionNumber: '361406',
          type: 'payment',
          status: 'settled',
          transactionAmount: 100,
          transactionCurrency: 'EUR',
          merchant: {
            name: 'Barclays St Johns Wood',
            id: 'be10579f-4274-43d9-8c8d-867111941209',
          },
          totalAmount: 100,
          creationDateTime: '2024-11-22T16:56:08.095396',
        },
        {
          id: '1e70c6dc-477d-4f61-aece-ff5c6081ed8e',
          cardId: '5fed064a-8cd8-4a84-8dfc-1bc92e2fc650',
          matchingTransactionId: 'CR241122000019',
          transactionNumber: '361089',
          type: 'refund',
          status: 'settled',
          transactionAmount: 100,
          transactionCurrency: 'EUR',
          merchant: {
            name: 'Acme Retail Limited',
            id: 'be10579f-4274-43d9-8c8d-867111941209',
          },
          totalAmount: 100,
          creationDateTime: '2024-11-21T14:16:40.478388',
        },
        {
          id: '7c9c127d-282a-44fd-abee-a27f11c27a6d',
          cardId: '5fed064a-8cd8-4a84-8dfc-1bc92e2fc650',
          matchingTransactionId: 'CR241122000018',
          transactionNumber: '361088',
          type: 'money-transfer',
          status: 'settled',
          transactionAmount: 10,
          transactionCurrency: 'EUR',
          originalCurrencyAmount: 100,
          originalCurrency: 'USD',
          merchant: {
            name: 'Acme Retail Limited',
            id: 'be10579f-4274-43d9-8c8d-867111941209',
          },
          totalAmount: 10,
          creationDateTime: '2024-11-21T14:16:40.117872',
        },
      ],
      limit: 5,
      page: 1,
      totalPages: 4,
      totalItems: 20,
    } as PaginatedResponse<CardTransactionInfoType>);
  }

  public getcCardTransactionDetailById(): Observable<CardTransactionDetail> {
    return of({
      id: '4ebd8a5a-2e73-448f-b97b-a57b2a17ef1a',
      type: 'payment',
      status: 'settled',
      date: '2024-11-29T12:09:04.138310',
      description: 'Transfer to own account, top-up Card **** 6358',
      transactionInfo: {
        amount: '10.00',
        currency: 'USD',
        totalAmount: '10.00',
        originalAmount: '10.00',
        originalAmountCurrency: 'GBP',
      },
      feeInfo: {
        feeAmount: '20',
        feeCurrency: null,
        atmFeeAmount: null,
        atmFeeCurrency: null,
        exchangeRate: null,
        exchangeRateCurrency: null,
        ecbMarkup: '78',
      },
      merchantInfo: {
        id: 'be10579f-4274-43d9-8c8d-867111941209',
        name: 'Acme Retail Limited',
        streetNumber: null,
        address: null,
        city: 'LONDON',
        country: 'GBR',
        postalCode: 'SW1 3AN',
        image: null,
        phone: null,
      },
    } as CardTransactionDetail);
  }
}
